import { useEffect, useRef, useState } from 'react'
import { attachmentContentUrl } from '../../api/endpoints'
import type { Attachment } from '../../api/types'
import { Icon } from '../../components/Icon'
import { formatDuration, knownDuration } from '../../lib/media'
import { ambientAudio, playbackAudio } from '../../sound/audioSession'
import { BlurhashCanvas } from '../conversation/BlurhashCanvas'
import { ClipFailed } from './ClipFailed'
import { clipVersion, clipView } from './clipView'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { MOBILE_QUERY } from '../../lib/platform'

interface Props {
  attachment: Attachment
  /** Still in the outbox. */
  pending: boolean
  /** Sent by me, so I may fetch it before it's converted. */
  mine: boolean
}

/** A video's frame: its own shape once converted, kept between a square-ish minimum and a tall maximum by CSS. */
function aspectRatio(attachment: Attachment): string {
  return attachment.width && attachment.height ? `${attachment.width} / ${attachment.height}` : '16 / 9'
}

/**
 * A video clip (spec §8): its first frame with its length and a play button, playing in the message with the browser's
 * controls. Nothing downloads until someone plays it. Until it's converted others see that it's processing.
 */
export function VideoAttachment({ attachment, pending, mine }: Props) {
  const view = clipView(attachment, { pending, mine })
  if (view.state === 'failed') return <ClipFailed attachment={attachment} download={view.download} />
  if (view.state === 'sending' || (view.state === 'processing' && !view.playable)) {
    const duration = knownDuration(attachment.durationMs)
    return (
      <div className="video-card">
        <div className="video-frame is-waiting" style={{ aspectRatio: aspectRatio(attachment) }}>
          <span className="spinner" aria-hidden="true" />
          <span>{view.state === 'sending' ? 'Sending…' : 'Processing video…'}</span>
          {duration !== null ? <span className="video-length">{formatDuration(duration)}</span> : null}
        </div>
      </div>
    )
  }
  return <VideoPlayer key={clipVersion(attachment)} attachment={attachment} processing={view.state === 'processing'} />
}

function VideoPlayer({ attachment, processing }: { attachment: Attachment; processing: boolean }) {
  // A phone plays it full screen in its own player, as the Photos app and WhatsApp do: an iPhone does so when the video
  // may not play inline, Android when asked. A computer plays it in place.
  const phone = useMediaQuery(MOBILE_QUERY)
  const video = useRef<HTMLVideoElement>(null)
  const playButton = useRef<HTMLButtonElement>(null)
  const [started, setStarted] = useState(false)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [posterShown, setPosterShown] = useState(false)
  const [posterFailed, setPosterFailed] = useState(false)
  const poster = attachment.hasThumb && !posterFailed ? attachmentContentUrl(attachment.id, 'thumb') : null
  const duration = knownDuration(attachment.durationMs)

  const start = () => {
    const element = video.current
    if (!element) return
    // Straight from the tap, which browsers need before playing sound; its controls take over from the button.
    element.controls = true
    setStarted(true)
    element.focus({ preventScroll: true })
    // Out loud on an iPhone with its silent switch on, like any clip someone starts.
    playbackAudio()
    if (phone) void element.requestFullscreen?.().catch(() => {})
    element.play().catch((error: unknown) => {
      ambientAudio()
      // Paused again at once (another clip started), or the browser wants a tap on its own controls: nothing is wrong.
      if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return
      setFailed(true)
    })
  }

  // Trying again: focus goes back to play, where the button that was pressed has gone.
  useEffect(() => {
    if (attempt > 0) playButton.current?.focus()
  }, [attempt])

  return (
    <div className="video-card">
      <div className="video-frame" style={{ aspectRatio: aspectRatio(attachment) }}>
        <video
          key={attempt}
          ref={video}
          src={attachmentContentUrl(attachment.id, 'original')}
          preload="none"
          playsInline={!phone}
          controls={started}
          tabIndex={started ? undefined : -1}
          aria-hidden={started ? undefined : true}
          aria-label={attachment.fileName}
          onError={() => setFailed(true)}
        />
        {!started ? (
          <button
            ref={playButton}
            type="button"
            className="video-poster"
            aria-label={`Play video${duration !== null ? `, ${formatDuration(duration)}` : ''}`}
            onClick={start}
          >
            {attachment.blurhash && !posterShown ? <BlurhashCanvas hash={attachment.blurhash} /> : null}
            {poster ? (
              <img
                src={poster}
                alt=""
                decoding="async"
                className={posterShown ? 'loaded' : undefined}
                onLoad={() => setPosterShown(true)}
                onError={() => setPosterFailed(true)}
              />
            ) : null}
            <span className="video-play" aria-hidden="true">
              <Icon name="play" />
            </span>
            {duration !== null ? <span className="video-length">{formatDuration(duration)}</span> : null}
          </button>
        ) : null}
      </div>
      {failed ? (
        <p className="clip-caption is-error">
          Couldn't play this video.{' '}
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setFailed(false)
              setStarted(false)
              setAttempt((n) => n + 1)
            }}
          >
            Try again
          </button>
        </p>
      ) : processing ? (
        <p className="clip-caption">Processing video…</p>
      ) : null}
    </div>
  )
}
